import { useState, useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from './contexts/AuthContext'
import { useMembership } from './hooks/useMembership'
import { useNotification } from './contexts/NotificationContext'
import { supabase } from './lib/supabase'
import logo from "../traderhub24-logo.svg"
import Footer from './components/Footer'

interface Strategy {
  id: string
  user_id: string
  name: string
  version: number
  display_name: string
  description: string | null
  entry_rules: string | null
  exit_rules: string | null
  version_notes: string | null
  image_url: string | null
  is_public: boolean
  is_active: boolean
  is_locked: boolean
  created_at: string
  updated_at: string
}

function Strategies({ onBack, setCurrentPage, embedded }: { onBack: () => void; setCurrentPage: (page: "home" | "changelog" | "all-trades" | "analytics" | "tools" | "strategies" | "social-trade" | "account" | "community") => void; embedded?: boolean }) {
  const { user, signOut } = useAuth()
  const { isPro, isFree, membershipType, profileLoading, canCreateStrategyVersions, maxStrategies } = useMembership()
  const { showWarning, showConfirm } = useNotification()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [strategies, setStrategies] = useState<Strategy[]>([])
  const [strategyStats, setStrategyStats] = useState<Record<string, { totalTrades: number; winRate: number; totalPnl: number }>>({})
  const [expandedStrategies, setExpandedStrategies] = useState<Set<string>>(new Set())
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showStrategyLimitReachedModal, setShowStrategyLimitReachedModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [editingStrategy, setEditingStrategy] = useState<Strategy | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [activeCount, setActiveCount] = useState(0)
  const [searchTerm, setSearchTerm] = useState('')
  const userMenuRef = useRef<HTMLDivElement>(null)
  const [showUserMenu, setShowUserMenu] = useState(false)
  const [showMobileMenu, setShowMobileMenu] = useState(false)
  const [isInfoExpanded, setIsInfoExpanded] = useState(false)

  // Form state
  const [formName, setFormName] = useState('')
  const [formDescription, setFormDescription] = useState('')
  const [formEntryRules, setFormEntryRules] = useState('')
  const [formExitRules, setFormExitRules] = useState('')
  const [formImage, setFormImage] = useState<File | null>(null)
  const [formImagePreview, setFormImagePreview] = useState<string | null>(null)
  const [formIsPublic, setFormIsPublic] = useState(false)
  const [formIsActive, setFormIsActive] = useState(true)
  const [uploading, setUploading] = useState(false)

  useEffect(() => {
    if (user) {
      fetchStrategies()
    }
  }, [user])

  // Redirect ?openCreate=1 to create-strategy page (backward compatibility)
  useEffect(() => {
    if (searchParams.get('openCreate') === '1' && user) {
      navigate('/app/create-strategy', { replace: true })
    }
  }, [searchParams, user, navigate])

  useEffect(() => {
    if (strategies.length > 0 && user) {
      fetchStrategyStats()
    }
  }, [strategies, user])

  const fetchStrategyStats = async () => {
    if (!user) return

    try {
      // Get all strategy IDs
      const strategyIds = strategies.map(s => s.id)
      if (strategyIds.length === 0) return

      // Fetch trades for all strategies
      const { data: trades, error } = await supabase
        .from('trades_with_market_data')
        .select('strategy_id, exit_date, pnl')
        .in('strategy_id', strategyIds)

      if (error) throw error

      // Calculate stats per strategy name (grouping all versions)
      const stats: Record<string, { totalTrades: number; winRate: number; totalPnl: number }> = {}
      
      strategies.forEach(strategy => {
        if (!stats[strategy.name]) {
          stats[strategy.name] = { totalTrades: 0, winRate: 0, totalPnl: 0 }
        }
      })

      // Group trades by strategy name (all versions together)
      const tradesByStrategyName: Record<string, typeof trades> = {}
      strategies.forEach(strategy => {
        if (!tradesByStrategyName[strategy.name]) {
          tradesByStrategyName[strategy.name] = []
        }
      })

      trades?.forEach(trade => {
        const strategy = strategies.find(s => s.id === trade.strategy_id)
        if (strategy && tradesByStrategyName[strategy.name]) {
          tradesByStrategyName[strategy.name].push(trade)
        }
      })

      // Calculate stats for each strategy name
      Object.keys(tradesByStrategyName).forEach(strategyName => {
        const strategyTrades = tradesByStrategyName[strategyName] || []
        const closedTrades = strategyTrades.filter(t => t.exit_date && t.pnl !== null)
        const winningTrades = closedTrades.filter(t => t.pnl !== null && t.pnl > 0)
        const totalPnl = closedTrades.reduce((sum, t) => sum + (t.pnl || 0), 0)
        const winRate = closedTrades.length > 0 ? (winningTrades.length / closedTrades.length) * 100 : 0

        stats[strategyName] = {
          totalTrades: strategyTrades.length,
          winRate,
          totalPnl
        }
      })

      setStrategyStats(stats)
    } catch (err: any) {
      console.error('Failed to fetch strategy stats:', err)
    }
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false)
      }
    }

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showUserMenu])

  const fetchStrategies = async () => {
    if (!user) return

    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('strategies')
        .select('*')
        .eq('user_id', user.id)
        .order('name', { ascending: true })
        .order('version', { ascending: true })

      if (error) throw error

      setStrategies(data || [])
      
      // Count active strategies
      const active = (data || []).filter(s => s.is_active).length
      setActiveCount(active)
      setError(null)
    } catch (err: any) {
      setError(err.message || 'Failed to load strategies')
    } finally {
      setLoading(false)
    }
  }

  const handleEditStrategy = async () => {
    if (!user || !editingStrategy) return

    // Validation
    if (!formName.trim()) {
      setError('Strategy name is required')
      return
    }

    // Check strategy limit from admin panel (only if activating)
    if (maxStrategies != null && formIsActive && !editingStrategy.is_active && activeCount >= maxStrategies) {
      setError(`Active strategy limit (${maxStrategies}) reached. Deactivate another strategy or upgrade to Pro.`)
      return
    }

    try {
      setUploading(true)
      setError(null)

      let imageUrl = editingStrategy.image_url

      // Handle image upload/replacement (pro only)
      if (formImage && isPro) {
        // Delete old image if exists
        if (editingStrategy.image_url) {
          const oldPath = editingStrategy.image_url.split('/').slice(-2).join('/')
          await supabase.storage
            .from('strategy-images')
            .remove([oldPath])
        }

        // Upload new image
        const fileExt = formImage.name.split('.').pop()
        const fileName = `${user.id}/${Date.now()}.${fileExt}`
        const filePath = `strategy-images/${fileName}`

        const { error: uploadError } = await supabase.storage
          .from('strategy-images')
          .upload(filePath, formImage, {
            cacheControl: '3600',
            upsert: false
          })

        if (uploadError) throw uploadError

        const { data: urlData } = supabase.storage
          .from('strategy-images')
          .getPublicUrl(filePath)

        imageUrl = urlData.publicUrl
      }

      // Update strategy
      const { error } = await supabase
        .from('strategies')
        .update({
          name: formName.trim(),
          description: formDescription.trim() || null,
          entry_rules: formEntryRules.trim() || null,
          exit_rules: formExitRules.trim() || null,
          image_url: imageUrl,
          is_public: isPro ? formIsPublic : false,
          is_active: formIsActive
        })
        .eq('id', editingStrategy.id)

      if (error) throw error

      await fetchStrategies()
      resetForm()
      setShowEditModal(false)
      setEditingStrategy(null)
    } catch (err: any) {
      setError(err.message || 'Failed to update strategy')
    } finally {
      setUploading(false)
    }
  }

  const handleDeleteStrategy = async (id: string) => {
    const confirmed = await showConfirm({
      title: 'Delete Strategy',
      message: 'Are you sure you want to delete this strategy? This action cannot be undone.',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      confirmButtonStyle: 'danger'
    })
    if (!confirmed) {
      return
    }

    try {
      setDeletingId(id)
      
      // Get strategy to delete image if exists
      const strategy = strategies.find(s => s.id === id)
      if (strategy?.image_url) {
        const pathParts = strategy.image_url.split('/')
        const fileName = pathParts[pathParts.length - 1]
        const filePath = `${strategy.user_id}/${fileName}`
        await supabase.storage
          .from('strategy-images')
          .remove([filePath])
      }

      const { error } = await supabase
        .from('strategies')
        .delete()
        .eq('id', id)

      if (error) throw error

      await fetchStrategies()
    } catch (err: any) {
      setError(err.message || 'Failed to delete strategy')
    } finally {
      setDeletingId(null)
    }
  }

  const handleCreateVersion = async (strategy: Strategy) => {
    if (!canCreateStrategyVersions) return

    const allVersions = strategies.filter(s => s.name === strategy.name)
    const willLockVersions = allVersions.filter(s => s.version <= strategy.version)

    if (willLockVersions.length > 0) {
      const confirmMessage = `Yeni versiyon oluşturduğunuzda, mevcut ve eski versiyonlar (${willLockVersions.length} versiyon) düzenlenemez hale gelecektir. Bu işlem geri alınamaz.\n\nDevam etmek istiyor musunuz?`
      const confirmed = await showConfirm({
        title: 'Create New Version',
        message: confirmMessage,
        confirmText: 'Create Version',
        cancelText: 'Cancel',
        confirmButtonStyle: 'primary'
      })
      if (!confirmed) return
    }

    navigate('/app/create-strategy', { state: { baseStrategy: strategy } })
  }

  const handleOpenCreateStrategy = () => {
    if (maxStrategies != null && activeCount >= maxStrategies) {
      setShowStrategyLimitReachedModal(true)
      return
    }
    navigate('/app/create-strategy')
  }

  const goToAccount = () => {
    setShowStrategyLimitReachedModal(false)
    if (embedded) {
      setCurrentPage('account')
    } else {
      navigate('/app/account')
    }
  }

  const openEditModal = (strategy: Strategy) => {
    // Check if strategy is locked
    if (strategy.is_locked) {
      const allVersions = strategies.filter(s => s.name === strategy.name)
      const newerVersions = allVersions.filter(s => s.version > strategy.version)
      showWarning(`Bu strateji versiyonu kilitlenmiştir ve düzenlenemez. Daha yeni versiyonlar mevcut (${newerVersions.length} versiyon). Lütfen en son versiyonu düzenleyin.`)
      return
    }

    setEditingStrategy(strategy)
    setFormName(strategy.name)
    setFormDescription(strategy.description || '')
    setFormEntryRules(strategy.entry_rules || '')
    setFormExitRules(strategy.exit_rules || '')
    setFormIsPublic(strategy.is_public)
    setFormIsActive(strategy.is_active)
    setFormImage(null)
    setFormImagePreview(strategy.image_url)
    setShowEditModal(true)
  }

  const resetForm = () => {
    setFormName('')
    setFormDescription('')
    setFormEntryRules('')
    setFormExitRules('')
    setFormImage(null)
    setFormImagePreview(null)
    setFormIsPublic(false)
    setFormIsActive(true)
    setError(null)
  }

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError('Image size must be less than 5MB')
        return
      }
      setFormImage(file)
      const reader = new FileReader()
      reader.onloadend = () => {
        setFormImagePreview(reader.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleRemoveImage = () => {
    setFormImage(null)
    setFormImagePreview(null)
  }

  const filteredStrategies = strategies.filter(strategy =>
    strategy.display_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (strategy.description && strategy.description.toLowerCase().includes(searchTerm.toLowerCase()))
  )

  // Group strategies by name and sort versions
  const groupedStrategies = filteredStrategies.reduce((acc, strategy) => {
    if (!acc[strategy.name]) {
      acc[strategy.name] = []
    }
    acc[strategy.name].push(strategy)
    return acc
  }, {} as Record<string, Strategy[]>)

  // Sort versions within each group
  Object.keys(groupedStrategies).forEach(name => {
    groupedStrategies[name].sort((a, b) => a.version - b.version)
  })

  const toggleExpand = (strategyName: string) => {
    const newExpanded = new Set(expandedStrategies)
    if (newExpanded.has(strategyName)) {
      newExpanded.delete(strategyName)
    } else {
      newExpanded.add(strategyName)
    }
    setExpandedStrategies(newExpanded)
  }

  const handleStrategyClick = (strategyName: string) => {
    const encodedName = encodeURIComponent(strategyName)
    navigate(`/app/strategy/${encodedName}`)
  }

  if (!user && !embedded) {
    return (
      <div className="min-h-screen bg-app-bg text-gray-900">
        <header className="w-full py-6 px-6 max-w-6xl mx-auto">
          <div className="flex items-center justify-between">
            <img 
              src={logo} 
              alt="TraderHub24" 
              className="h-20 w-auto cursor-pointer hover:opacity-80 transition-opacity" 
              onClick={onBack}
            />
          </div>
        </header>
        <main className="max-w-6xl mx-auto px-6 py-6">
          <div className="bg-app-box shadow-sm border border-gray-200 rounded-2xl p-6">
            <p className="text-gray-500 text-center">Please sign in to view your strategies</p>
          </div>
        </main>
      </div>
    )
  }

  return (
    <div className={embedded ? "dashboard-page-content" : "min-h-screen bg-app-bg text-gray-900"}>
      {!embedded && (
      <header className="w-full py-3 px-4 md:px-6 max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <img 
            src={logo} 
            alt="TraderHub24" 
            className="h-12 md:h-20 w-auto cursor-pointer hover:opacity-80 transition-opacity" 
            onClick={onBack}
          />
          <div className="flex items-center gap-2">
            <div className="relative flex items-center gap-2">
              {/* Mobile Menu Button */}
              <button
                onClick={() => setShowMobileMenu(!showMobileMenu)}
                className="md:hidden p-2 text-gray-700 hover:bg-gray-100 rounded-lg border border-gray-300"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {showMobileMenu ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
                  )}
                </svg>
              </button>

              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="px-3 md:px-4 py-2 text-gray-700 font-medium rounded-lg hover:bg-gray-100 transition-colors flex items-center gap-2 border border-gray-300"
                  title="Account"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <span className="hidden md:inline">Account</span>
                </button>
                {showUserMenu && (
                  <div className="absolute right-0 mt-2 w-64 bg-app-box rounded-lg shadow-lg border border-gray-200 z-50">
                    <div className="p-4 border-b border-gray-200">
                      <p className="text-sm font-semibold text-gray-900">Account</p>
                      <p className="text-sm text-gray-600 mt-1 truncate">{user.email}</p>
                      <div className="mt-2 flex items-center gap-2">
                        <span className="text-xs text-gray-500">Membership</span>
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                            membershipType === 'pro'
                              ? 'bg-purple-100 text-purple-700'
                              : 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {profileLoading ? 'Loading...' : membershipType.toUpperCase()}
                        </span>
                      </div>
                    </div>
                    <div className="p-2">
                      <button
                        onClick={() => {
                          navigate("/account")
                          setShowUserMenu(false);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors flex items-center gap-2"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4a4 4 0 100 8 4 4 0 000-8zM6 20a6 6 0 0112 0" />
                        </svg>
                        Account Settings
                      </button>
                      <button
                        onClick={() => {
                          signOut();
                          setShowUserMenu(false);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors flex items-center gap-2"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                        Sign Out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Sidebar/Menu */}
        {showMobileMenu && user && (
          <div className="md:hidden fixed inset-0 z-[100] flex">
            <div className="fixed inset-0 bg-black/50" onClick={() => setShowMobileMenu(false)} />
            <div className="relative w-64 max-w-[80%] h-full bg-app-box shadow-xl flex flex-col p-4 animate-in slide-in-from-left duration-300">
              <div className="flex items-center justify-between mb-8">
                <img src={logo} alt="TraderHub24" className="h-8 w-auto" />
                <button onClick={() => setShowMobileMenu(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <nav className="space-y-2">
                {[
                  { id: "all-trades", label: "All Trades", icon: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" },
                  { id: "analytics", label: "Analytics", icon: "M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" },
                  { id: "tools", label: "Tools", icon: "M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a1 1 0 01-1-1v-3a1 1 0 011-1h1a2 2 0 100-4H4a1 1 0 01-1-1V7a1 1 0 011-1h3a1 1 0 001-1V4z" },
                  { id: "strategies", label: "Strategies", icon: "M13 10V3L4 14h7v7l9-11h-7z" },
                  { id: "social-trade", label: "Social Trade", icon: "M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" },
                  { id: "community", label: "Community", icon: "M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" },
                ].map((item) => (
                  <button
                    key={item.id}
                    onClick={() => {
                      setCurrentPage(item.id as any);
                      setShowMobileMenu(false);
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                      item.id === "strategies" 
                        ? "bg-orange-50 text-orange-600" 
                        : "text-gray-700 hover:bg-gray-100"
                    }`}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={item.icon} />
                    </svg>
                    {item.label}
                  </button>
                ))}
                <button
                  onClick={() => {
                    navigate('/app/add-trade');
                    setShowMobileMenu(false);
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium bg-orange-500 text-white hover:bg-orange-600 transition-colors mt-4"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Add Trade
                </button>
              </nav>
            </div>
          </div>
        )}
      </header>
      )}

      {(() => {
        const W = embedded ? 'div' : 'main';
        return (
        <W className={embedded ? 'w-full px-4 md:px-6 py-6' : 'max-w-6xl mx-auto px-4 md:px-6 py-6'}>
        {!embedded && (
        <section className="hidden md:flex items-center justify-center gap-4 mb-6">
          <button
            onClick={() => setCurrentPage("home")}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-app-box border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-orange-500 hover:text-orange-500 transition-colors flex items-center justify-center"
            title="Home"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
          </button>
          <button
            onClick={() => navigate("/app/all-trades")}
            className="px-6 py-2 text-sm font-medium text-gray-700 bg-app-box border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-orange-500 hover:text-orange-500 transition-colors"
          >
            All Trades
          </button>
          <button
            onClick={() => navigate("/app/analytics")}
            className="px-6 py-2 text-sm font-medium text-gray-700 bg-app-box border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-orange-500 hover:text-orange-500 transition-colors"
          >
            Analytics
          </button>
          <button
            onClick={() => navigate("/app/tools")}
            className="px-6 py-2 text-sm font-medium text-gray-700 bg-app-box border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-orange-500 hover:text-orange-500 transition-colors"
          >
            Tools
          </button>
          <button
            onClick={() => navigate("/strategies")}
            className="px-6 py-2 text-sm font-medium text-white bg-orange-500 border border-orange-500 rounded-lg hover:bg-orange-600 transition-colors"
          >
            Strategies
          </button>
          <button
            onClick={() => navigate("/app/social-trade")}
            className="px-6 py-2 text-sm font-medium text-gray-700 bg-app-box border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-orange-500 hover:text-orange-500 transition-colors"
          >
            Social Trade
          </button>
          <button
            onClick={() => navigate("/app/community")}
            className="px-6 py-2 text-sm font-medium text-gray-700 bg-app-box border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-orange-500 hover:text-orange-500 transition-colors"
          >
            Community
          </button>
          {user && (
            <button
              onClick={() => navigate('/app/add-trade')}
              className="px-6 py-2 text-sm font-medium text-white bg-orange-500 border border-orange-500 rounded-lg hover:bg-orange-600 hover:border-orange-600 transition-colors"
            >
              Add Trade
            </button>
          )}
        </section>
        )}

        <div className="bg-app-box shadow-sm border border-gray-200 rounded-2xl p-4 md:p-6">
          {/* Header with Create Button and Active Count */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
            <div>
              <h2 className="text-xl md:text-2xl font-bold">My Strategies</h2>
              {maxStrategies != null ? (
                <p className="text-xs md:text-sm text-gray-500 mt-1">
                  Active: <strong>{activeCount} / {maxStrategies}</strong>
                </p>
              ) : (
                <p className="text-xs md:text-sm text-gray-500 mt-1">
                  Active: <strong>{activeCount}</strong> (Unlimited)
                </p>
              )}
            </div>
            <button
              onClick={handleOpenCreateStrategy}
              className="w-full sm:w-auto px-6 py-2.5 text-sm font-bold text-white bg-orange-500 border border-orange-500 rounded-lg hover:bg-orange-600 transition-colors shadow-sm"
            >
              + Create Strategy
            </button>
          </div>

          {/* Search */}
          <div className="mb-6">
            <div className="relative">
              <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </span>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search strategies by name or description..."
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm shadow-sm"
              />
            </div>
          </div>

          {/* Info Box */}
          <div className="mb-6 bg-blue-50 border border-blue-200 rounded-xl overflow-hidden">
            <button
              onClick={() => setIsInfoExpanded(!isInfoExpanded)}
              className="w-full flex items-center justify-between p-4 md:p-5 text-left hover:bg-blue-100 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-xs md:text-sm font-semibold text-blue-900 uppercase tracking-wide">Strategy Management Guide</h3>
              </div>
              <svg
                className={`w-5 h-5 text-blue-600 transition-transform ${isInfoExpanded ? 'transform rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {isInfoExpanded && (
              <div className="px-4 md:px-5 pb-5 animate-in fade-in slide-in-from-top-2 duration-200">
                <div className="text-xs md:text-sm text-blue-800 space-y-3 leading-relaxed">
                  <p>
                    <strong>Creating Strategies:</strong> Define name, description, entry and exit rules. 
                    {isPro ? ' Pro users can add images and make them public.' : ' Free users can only add descriptions.'}
                  </p>
                  {isPro ? (
                    <>
                      <p>
                        <strong>Version System (Pro):</strong> Track evolution with versions (e.g., "Strategy (1)", "(2)"). Compare version performance.
                      </p>
                      <p>
                        <strong>Version Locking:</strong> Creating a new version locks all older ones. Prevents changes to past data integrity. Only latest is editable.
                      </p>
                    </>
                  ) : (
                    <p>
                      <strong>Version System:</strong> Upgrade to Pro for versioning. Free users get one version (v0) per strategy.
                    </p>
                  )}
                  <p>
                    <strong>Active Strategy Limit:</strong> {maxStrategies != null ? `Max ${maxStrategies} active strategies (see your plan). Deactivate old ones to add new.` : 'Unlimited active strategies.'}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Error Display */}
          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          {/* Loading State */}
          {loading ? (
            <p className="text-gray-500 text-center py-8">Loading strategies...</p>
          ) : filteredStrategies.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500 mb-4">
                {searchTerm ? 'No strategies match your search.' : 'No strategies yet. Create your first strategy!'}
              </p>
              {!searchTerm && (
                <button
                  onClick={handleOpenCreateStrategy}
                  className="px-6 py-2 bg-orange-500 hover:bg-orange-600 text-white font-medium rounded-lg transition-colors"
                >
                  Create Strategy
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {Object.entries(groupedStrategies).map(([name, versions]) => {
                const mainStrategy = versions.find(v => v.version === 0) || versions[0]
                const stats = strategyStats[name] || { totalTrades: 0, winRate: 0, totalPnl: 0 }
                const isExpanded = expandedStrategies.has(name)
                const hasVersions = versions.length > 1

                return (
                  <div key={name} className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                    {/* Main Strategy Card */}
                    <div
                      className="p-4 md:p-5 hover:bg-gray-50/50 transition-colors cursor-pointer"
                      onClick={() => handleStrategyClick(name)}
                    >
                      <div className="flex flex-col sm:flex-row items-start gap-4">
                        {/* Image Preview */}
                        {mainStrategy.image_url && (
                          <div className="w-full sm:w-24 h-32 sm:h-24 flex-shrink-0">
                            <img
                              src={mainStrategy.image_url}
                              alt={mainStrategy.name}
                              className="w-full h-full object-cover rounded-lg shadow-inner"
                            />
                          </div>
                        )}

                        {/* Strategy Info */}
                        <div className="flex-1 min-w-0 w-full">
                          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                            <h3 className="text-lg font-bold text-gray-900 truncate pr-2">{name}</h3>
                            <div className="flex items-center gap-2">
                              {mainStrategy.is_public && (
                                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-100 text-blue-700 uppercase tracking-wide">
                                  Public
                                </span>
                              )}
                              {mainStrategy.is_active ? (
                                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-green-100 text-green-700 uppercase tracking-wide">
                                  Active
                                </span>
                              ) : (
                                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-gray-100 text-gray-700 uppercase tracking-wide">
                                  Inactive
                                </span>
                              )}
                            </div>
                          </div>

                          {mainStrategy.description && (
                            <p className="text-xs md:text-sm text-gray-600 mb-4 line-clamp-2 leading-relaxed italic md:not-italic">
                              {mainStrategy.description}
                            </p>
                          )}

                          {/* Statistics Grid */}
                          <div className="grid grid-cols-3 gap-2 md:gap-4 mb-4 bg-gray-50 p-3 rounded-lg border border-gray-100">
                            <div className="text-center">
                              <p className="text-[10px] text-gray-500 uppercase font-semibold">Trades</p>
                              <p className="text-sm font-bold text-gray-900">{stats.totalTrades}</p>
                            </div>
                            <div className="text-center border-x border-gray-200 px-2">
                              <p className="text-[10px] text-gray-500 uppercase font-semibold">Win %</p>
                              <p className="text-sm font-bold text-orange-600">{stats.winRate.toFixed(1)}%</p>
                            </div>
                            <div className="text-center">
                              <p className="text-[10px] text-gray-500 uppercase font-semibold">P&L</p>
                              <p className={`text-sm font-bold ${stats.totalPnl >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {stats.totalPnl >= 0 ? '+' : ''}{stats.totalPnl.toFixed(2)}
                              </p>
                            </div>
                          </div>

                          {/* Versions indicator */}
                          {hasVersions && (
                            <div className="flex items-center justify-between text-[10px] md:text-xs text-gray-500 border-t border-gray-100 pt-3">
                              <span><strong>{versions.length}</strong> versions documented</span>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  toggleExpand(name)
                                }}
                                className="flex items-center gap-1 font-bold text-orange-600 hover:text-orange-700 transition-colors uppercase tracking-wider"
                              >
                                {isExpanded ? (
                                  <>Hide History <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 15l7-7 7 7" /></svg></>
                                ) : (
                                  <>Show History <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M19 9l-7 7-7-7" /></svg></>
                                )}
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Versions List (Collapsible) */}
                    {hasVersions && isExpanded && (
                      <div className="border-t border-gray-200 bg-gray-50/50">
                        <div className="p-3 md:p-4 space-y-3">
                          {versions.map((strategy) => (
                            <div
                              key={strategy.id}
                              className="bg-white rounded-lg p-3 border border-gray-200 shadow-sm"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-2">
                                <div className="flex flex-wrap items-center gap-2">
                                  <h4 className="font-bold text-sm text-gray-900 uppercase tracking-wide">{strategy.display_name}</h4>
                                  {strategy.is_locked && (
                                    <span className="px-2 py-0.5 rounded-full text-[9px] font-bold bg-gray-100 text-gray-600 uppercase tracking-tighter" title="Locked version">
                                      🔒 Locked
                                    </span>
                                  )}
                                  {strategy.is_public && (
                                    <span className="px-2 py-0.5 rounded-full text-[9px] font-bold bg-blue-50 text-blue-600 uppercase tracking-tighter">
                                      Public
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center gap-1 sm:gap-2">
                                  <button
                                    onClick={() => openEditModal(strategy)}
                                    disabled={strategy.is_locked}
                                    className={`flex-1 sm:flex-none px-2.5 py-1 text-[10px] font-bold rounded uppercase transition-colors ${
                                      strategy.is_locked
                                        ? 'text-gray-400 cursor-not-allowed bg-gray-50'
                                        : 'text-orange-600 border border-orange-200 hover:bg-orange-50'
                                    }`}
                                  >
                                    Edit
                                  </button>
                                  {isPro && !strategy.is_locked && (
                                    <button
                                      onClick={() => handleCreateVersion(strategy)}
                                      className="flex-1 sm:flex-none px-2.5 py-1 text-[10px] font-bold text-blue-600 border border-blue-200 hover:bg-blue-50 rounded uppercase transition-colors"
                                    >
                                      Clone
                                    </button>
                                  )}
                                  <button
                                    onClick={() => handleDeleteStrategy(strategy.id)}
                                    disabled={deletingId === strategy.id}
                                    className="p-1.5 text-red-500 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h14" />
                                    </svg>
                                  </button>
                                </div>
                              </div>
                              {strategy.description && (
                                <p className="text-[11px] text-gray-500 italic line-clamp-1">{strategy.description}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Actions for main strategy (when not expanded) */}
                    {!isExpanded && (
                      <div
                        className="border-t border-gray-100 p-3 md:p-4 bg-gray-50/30 flex flex-wrap items-center gap-2"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          onClick={() => openEditModal(mainStrategy)}
                          disabled={mainStrategy.is_locked}
                          className={`px-3 py-1.5 text-[10px] md:text-xs font-bold rounded-lg uppercase transition-colors ${
                            mainStrategy.is_locked
                              ? 'text-gray-400 cursor-not-allowed bg-gray-100'
                              : 'text-orange-600 bg-white border border-orange-200 hover:bg-orange-50 shadow-sm'
                          }`}
                        >
                          Edit
                        </button>
                        {isPro && !mainStrategy.is_locked && (
                          <button
                            onClick={() => handleCreateVersion(mainStrategy)}
                            className="px-3 py-1.5 text-[10px] md:text-xs font-bold text-blue-600 bg-white border border-blue-200 hover:bg-blue-50 rounded-lg uppercase shadow-sm transition-colors"
                          >
                            New Ver
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteStrategy(mainStrategy.id)}
                          disabled={deletingId === mainStrategy.id}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h14" />
                          </svg>
                        </button>
                        <div className="flex-1 min-w-[10px]" />
                        <button
                          onClick={() => handleStrategyClick(name)}
                          className="px-4 py-1.5 text-[10px] md:text-xs font-bold text-white bg-orange-500 hover:bg-orange-600 rounded-lg shadow-sm transition-all active:scale-95 uppercase tracking-wider"
                        >
                          Details
                        </button>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>
        </W>
        );
      })()}

      {/* Strategy limit reached – upgrade modal */}
      {showStrategyLimitReachedModal && maxStrategies != null && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="rounded-2xl p-6 max-w-md w-full mx-4 shadow-xl bg-app-box border border-orange-200">
            <div className="flex justify-center mb-4">
              <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 text-center mb-2">Strategy limit reached</h2>
            <p className="text-sm text-gray-600 text-center mb-6">
              Your plan allows up to <strong>{maxStrategies}</strong> active strategies. Deactivate an existing strategy to add a new one, or upgrade to Pro for unlimited strategies.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => setShowStrategyLimitReachedModal(false)}
                className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
              <button
                onClick={goToAccount}
                className="flex-1 px-4 py-2.5 bg-orange-500 hover:bg-orange-600 text-white font-medium rounded-lg transition-colors"
              >
                Upgrade to Pro
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editingStrategy && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="rounded-2xl p-6 max-w-2xl w-full mx-4 my-8 shadow-xl max-h-[90vh] overflow-y-auto bg-app-box">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Edit Strategy</h2>
              <button
                onClick={() => {
                  resetForm()
                  setShowEditModal(false)
                  setEditingStrategy(null)
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Strategy Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="Enter strategy name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="Describe your strategy..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Entry Rules</label>
                <textarea
                  value={formEntryRules}
                  onChange={(e) => setFormEntryRules(e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="Define entry conditions and rules..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Exit Rules</label>
                <textarea
                  value={formExitRules}
                  onChange={(e) => setFormExitRules(e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="Define exit conditions and rules..."
                />
              </div>

              {isPro && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Image (Optional)</label>
                    {formImagePreview ? (
                      <div className="relative">
                        <img src={formImagePreview} alt="Preview" className="w-full h-48 object-cover rounded-lg" />
                        <button
                          onClick={handleRemoveImage}
                          className="absolute top-2 right-2 px-2 py-1 bg-red-500 text-white rounded-lg text-xs hover:bg-red-600"
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                      />
                    )}
                    {editingStrategy.image_url && !formImagePreview && (
                      <p className="text-xs text-gray-500 mt-1">Current image will be kept if no new image is uploaded</p>
                    )}
                  </div>

                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={formIsPublic}
                        onChange={(e) => setFormIsPublic(e.target.checked)}
                        className="rounded"
                      />
                      <span className="text-sm text-gray-700">Make public</span>
                    </label>
                  </div>
                </>
              )}

              <div className="flex items-center gap-2">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formIsActive}
                    onChange={(e) => setFormIsActive(e.target.checked)}
                    className="rounded"
                  />
                  <span className="text-sm text-gray-700">Active</span>
                </label>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => {
                    resetForm()
                    setShowEditModal(false)
                    setEditingStrategy(null)
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleEditStrategy}
                  disabled={uploading}
                  className="flex-1 bg-orange-500 hover:bg-orange-600 text-white font-medium py-2 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {uploading ? 'Updating...' : 'Update Strategy'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer currentPage={undefined} onNavigate={(page) => {
        if (page === "privacy-policy") navigate("/privacy");
        else if (page === "terms-and-conditions") navigate("/terms");
        else if (page === "risk-warning") navigate("/risk-warning");
        else if (page === "documentation") navigate("/documentation");
        else if (page === "contact") navigate("/contact");
        else if (page === "home") onBack();
        else if (page === "changelog") navigate("/changelog");
      }} />
    </div>
  )
}

export default Strategies

